import { Loader, Popover } from "@screencloud/screencloud-ui-components";
import classNames from "clsx";
import { useCallback } from "react";
import {
  FormattedMessage,
  WrappedComponentProps,
  injectIntl,
} from "react-intl";
import { useStateInnerWidth } from "../../../hooks/useStateInnerWidth";
import ImageLoader from "../../ImageLoader";
import { CountPopoverStyled, PopoverStyled, Styled } from "./styles";
import { useAppContext } from "src/hooks/useAppContext";
import { mapPlaylistToContentListPreview } from "src/helpers/playlistHelper";
import { Playlist, usePlaylistVisibleItemCountLazyQuery } from "src/types.g";
import { ListContentItem } from "src/constants/constants";

export interface PlaylistItemType {
  id: string;
  url: string;
  content: any;
  contentType: string;
  contentTitle: string;
}
export interface PlaylistItemsPreviewProps {
  className?: string;
  isInPicker?: boolean;
  playlist: Playlist;
  ["data-testid"]?: string;
  onPreviewClick: (content: any, contentType: string) => void;
  readOnly?: boolean;
}

/**
 * How long the user has to rest the cursor on the overflow indicator before we
 * ask the server how many items the playlist actually has. Deliberately long:
 * the count is no longer needed to render the row, so it should only ever be
 * fetched when someone actively wants it. See GRWBKK-1870.
 */
export const OVERFLOW_HOVER_DELAY_MS = 2000;

/**
 * The playlist listing fragment (`PlaylistByIdForPlaylistListing`) only fetches
 * the first 6 items of each content type for the tile preview (GRWBKK-1801, to
 * keep this list query cheap). We must never try to lay out more thumbnails than
 * that -- otherwise the strip under-fills on wide viewports and, worse, the
 * overflow "…" is gated against a threshold larger than what we actually show,
 * so a 7-to-20 item playlist reads as if it had only the handful on screen.
 */
export const TILE_PREVIEW_THUMBNAIL_LIMIT = 6;

export const calculatePreviewItemBreak = (
  width: number,
  isInPicker?: boolean,
) => {
  if (isInPicker) {
    return 4;
  } else if (width > 2559) {
    return 20;
  } else if (width > 1919) {
    return 12;
  } else if (width > 1599) {
    return 8;
  } else if (width > 1151) {
    return 6;
  } else {
    return 4;
  }
};

export const getContentLisByActiveIds = (
  list: ListContentItem[],
  ids: string[],
) => {
  if (list.length === 0) {
    return [];
  }
  if (ids.length === 0) {
    return list;
  }
  const orderedList: ListContentItem[] = [];
  const remainingItems: ListContentItem[] = [];

  for (const item of list) {
    if (ids.includes(item.content._ref.id)) {
      orderedList.push(item);
    } else {
      remainingItems.push(item);
    }
  }

  // Append the remaining items to the ordered list to maintain the total count
  orderedList.push(...remainingItems);

  return orderedList;
};

const getActiveContentInPlaylist = (playlist: Playlist) => {
  return [
    ...(playlist.filesByPlaylistId?.nodes ?? []).map((item) => item.id),
    ...(playlist.appInstancesByPlaylistId?.nodes ?? []).map((item) => item.id),
    ...(playlist.linksByPlaylistId?.nodes ?? []).map((item) => item.id),
    ...(playlist.sitesByPlaylistId?.nodes ?? []).map((item) => item.id),
  ];
};

const PlaylistItemsPreview = (
  props: PlaylistItemsPreviewProps & WrappedComponentProps,
): JSX.Element => {
  const { className, playlist, isInPicker, intl } = props;

  const width = useStateInnerWidth();
  const context = useAppContext();

  /**
   * The exact item count is only fetched when the user hovers the overflow
   * indicator. It is an authorization-dependent value (the count of items
   * visible to this viewer), so `cache-and-network` rather than `cache-first`:
   * a re-hover still renders instantly from cache, while the network response
   * corrects a count gone stale after a share or permission change in-session.
   */
  const [fetchVisibleItemCount, { data, loading, error }] =
    usePlaylistVisibleItemCountLazyQuery({ fetchPolicy: "cache-and-network" });

  /**
   * The responsive breakpoint tells us how many thumbnails the viewport could
   * hold, but the row only ever has `first: 6` of each type fetched, so cap the
   * layout to what we actually have. This keeps the thumbnail slice and the
   * overflow gate below in step with the fetch.
   */
  const previewItemBreak = Math.min(
    calculatePreviewItemBreak(width, isInPicker),
    TILE_PREVIEW_THUMBNAIL_LIMIT,
  );

  /**
   * this is used to calculate the order of the items in the playlist (playlist got only 6 items for each type to preview)
   * to be more clear, search for PlaylistByIdForPlaylistListing fragment
   */
  const activeContentInPlaylist = getActiveContentInPlaylist(playlist);

  /**
   * need to grab the content base on activeContentInPlaylist into the beginning of the list
   *
   * example:
   * playlist.content.list = [10,6,3,1,5,2]
   * activeContentInPlaylist = [1,2,3]
   * orderedContentList = [3,1,2,10,6,5]
   * 10, 6, 5 may not be in the activeContentInPlaylist because it is not in the first 6 items of the content
   * so we need to append it to the end of the list
   */
  const contentList: ListContentItem[] = playlist.content?.list ?? [];
  const orderedContentList = getContentLisByActiveIds(
    contentList,
    activeContentInPlaylist,
  );

  const showThumbnail = mapPlaylistToContentListPreview(
    orderedContentList.slice(0, previewItemBreak),
    playlist,
    context.secureMediaPolicy,
  );

  /**
   * Whether there is anything beyond the thumbnails we render. Gated against the
   * capped `previewItemBreak` (i.e. the number of thumbnails actually laid out),
   * so the "…" appears whenever the playlist holds more than the strip shows.
   * Derived from the playlist row we already have, so the list costs no extra
   * query -- the exact number behind the indicator is resolved on hover instead.
   */
  const hasOverflow = contentList.length > previewItemBreak;

  const handleCountPopoverOpen = useCallback(() => {
    if (!playlist?.id) {
      return;
    }
    fetchVisibleItemCount({ variables: { playlistId: playlist.id } });
  }, [fetchVisibleItemCount, playlist?.id]);

  const renderItemCount = () => {
    const count = data?.playlistVisibleItemCount;

    if (loading || (!error && count === undefined)) {
      return <Loader size="small" inline active inverted />;
    }

    if (error || count === null || count === undefined) {
      return (
        <FormattedMessage
          id="playlist.label.item_count_unavailable"
          defaultMessage="Count unavailable"
        />
      );
    }

    return (
      <FormattedMessage
        id="playlist.label.item_count"
        defaultMessage="{count, plural, one {# item} other {# items}}"
        values={{ count }}
      />
    );
  };

  let previewLeftMargin = 0;
  const previewContents = showThumbnail.map((item, index) => {
    const thumbnailSource = item?.content.fileByThumbnailFileId?.source;
    const iconUrl = (item?.url || thumbnailSource) ?? "";
    previewLeftMargin = index * 15;
    return (
      <Popover
        key={`playlist-preview-firt-row-${index}`}
        inverted
        trigger={
          <div
            onClick={() =>
              !props.readOnly &&
              props.onPreviewClick(item?.content, item?.contentType!)
            }
            className={classNames("trigger-preview", {
              ["preview-read-only"]: props.readOnly,
            })}
            style={{
              zIndex: showThumbnail.length - index,
            }}
          >
            <ImageLoader src={iconUrl} title={item?.contentTitle} />
          </div>
        }
        content={
          <PopoverStyled>
            <img src={iconUrl} title={item?.contentTitle} />
          </PopoverStyled>
        }
        className="playlist-item-thumbnail"
        position="top center"
      />
    );
  });
  return (
    <Styled
      data-testid={props["data-testid"] ?? "playlist-items-preview"}
      className={className}
    >
      {previewContents}
      {hasOverflow && (
        <Popover
          inverted
          on={["hover", "focus"]}
          mouseEnterDelay={OVERFLOW_HOVER_DELAY_MS}
          onOpen={handleCountPopoverOpen}
          position="top center"
          className="playlist-item-count"
          trigger={
            <span
              className="more-preview"
              data-testid="playlist-items-preview-more"
              tabIndex={0}
              aria-label={intl.formatMessage({
                id: "playlist.label.show_item_count",
                defaultMessage: "Show item count",
              })}
              style={{ left: previewLeftMargin + 45 + "px" }}
            >
              <span className="more-preview__label">&hellip;</span>
            </span>
          }
          content={
            <CountPopoverStyled data-testid="playlist-items-preview-count">
              {renderItemCount()}
            </CountPopoverStyled>
          }
        />
      )}
    </Styled>
  );
};

export default injectIntl(PlaylistItemsPreview);
